import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Users, 
  LogOut, 
  Search, 
  Filter,
  Mail,
  Phone,
  MapPin,
  Instagram,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  Clock,
  XCircle,
  Eye,
  CalendarCheck,
  CalendarX,
  Video,
  Handshake,
  ThumbsDown,
  User,
  UserX,
  Trash2,
  MessageSquare,
  Save,
  Star,
  FileText,
  Upload,
  Download,
  X,
  UserPlus,
  Globe,
  ToggleLeft,
  ToggleRight,
  Copy,
  Link as LinkIcon,
  Edit3,
  Image,
  AtSign,
  FileStack,
  Settings,
  UserCheck,
  Lock,
  RefreshCw,
  History,
  ArrowRight,
  Package,
  CreditCard
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;
const API = `${BACKEND_URL}/api`;

// Helper to get correct image data URI
const getImageSrc = (base64Data) => {
  if (!base64Data) return null;
  if (base64Data.startsWith("data:")) return base64Data;
  
  // Detect image type from base64 signature
  if (base64Data.startsWith("UklGR")) {
    return `data:image/webp;base64,${base64Data}`;
  } else if (base64Data.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${base64Data}`;
  } else if (base64Data.startsWith("iVBOR")) {
    return `data:image/png;base64,${base64Data}`;
  }
  return `data:image/jpeg;base64,${base64Data}`;
};

// Lazy loading photo component with intersection observer
const LazyPhoto = ({ applicationId, photoData, isLoading, onVisible, name }) => {
  const ref = useRef(null);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    if (hasTriggered || photoData) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered) {
          setHasTriggered(true);
          onVisible();
        }
      },
      { rootMargin: '100px' }
    );
    
    if (ref.current) {
      observer.observe(ref.current);
    }
    
    return () => observer.disconnect();
  }, [hasTriggered, onVisible, photoData]);

  return (
    <div ref={ref} className="flex-shrink-0">
      {photoData ? (
        <img 
          src={getImageSrc(photoData)}
          alt={name}
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover border-2 border-gray-700"
        />
      ) : isLoading ? (
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-gray-700/50 border-2 border-gray-700 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-gray-500/30 border-t-gray-500 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-gray-700/50 border-2 border-gray-700 flex items-center justify-center">
          <User size={32} className="text-gray-600" />
        </div>
      )}
    </div>
  );
};

const statusConfig = {
  pending: { bg: "bg-amber-500/10", text: "text-amber-500", border: "border-amber-500/30", icon: Clock, label: "Pending" },
  contacted: { bg: "bg-blue-500/10", text: "text-blue-500", border: "border-blue-500/30", icon: Check, label: "Contacted" },
  meeting_scheduled: { bg: "bg-purple-500/10", text: "text-purple-500", border: "border-purple-500/30", icon: CalendarCheck, label: "Meeting Scheduled" },
  meeting_canceled: { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30", icon: CalendarX, label: "Meeting Canceled" },
  meeting_performed: { bg: "bg-indigo-500/10", text: "text-indigo-500", border: "border-indigo-500/30", icon: Video, label: "Meeting Performed" },
  no_show: { bg: "bg-rose-500/10", text: "text-rose-400", border: "border-rose-500/30", icon: UserX, label: "No Show" },
  deal: { bg: "bg-green-500/10", text: "text-green-500", border: "border-green-500/30", icon: Handshake, label: "Deal" },
  closed: { bg: "bg-gray-500/10", text: "text-gray-400", border: "border-gray-500/30", icon: XCircle, label: "Closed" },
  not_a_fit: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30", icon: ThumbsDown, label: "Not a Fit" },
  not_interested: { bg: "bg-slate-500/10", text: "text-slate-400", border: "border-slate-500/30", icon: XCircle, label: "Not Interested" }
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [applications, setApplications] = useState([]);
  const [photoCache, setPhotoCache] = useState({}); // Cache for lazy-loaded photos
  const [loadingPhotos, setLoadingPhotos] = useState({}); // Track which photos are loading
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [interestingFilter, setInterestingFilter] = useState("all"); // all, interesting, not_interesting
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [togglingInterestingId, setTogglingInterestingId] = useState(null);
  const [uploadingPdfId, setUploadingPdfId] = useState(null);
  const [deletingPdfId, setDeletingPdfId] = useState(null);
  const [creatingCreatorId, setCreatingCreatorId] = useState(null);
  const [creatorModal, setCreatorModal] = useState(null); // {id, email, name, temp_password, landing_page_slug}
  const [togglingLandingPageId, setTogglingLandingPageId] = useState(null);
  const [editCreatorModal, setEditCreatorModal] = useState(null); // Creator data for editing
  const [editCreatorForm, setEditCreatorForm] = useState({});
  const [savingCreator, setSavingCreator] = useState(false);
  const [handlingAppId, setHandlingAppId] = useState(null); // Application being handled/claimed
  const [managers, setManagers] = useState([]); // List of managers for reassignment dropdown
  const [reassigningAppId, setReassigningAppId] = useState(null); // Application being reassigned
  const [statusHistoryModal, setStatusHistoryModal] = useState(null); // {applicationId, history, loading}
  const pdfInputRefs = useRef({});
  const profileImageInputRef = useRef(null);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    
    const userData = JSON.parse(storedUser);
    if (!["admin", "manager"].includes(userData.role)) {
      navigate("/login");
      return;
    }
    
    setUser(userData);
    fetchApplications();
    // Admins fetch manager list for reassignment
    if (userData.role === "admin") {
      fetchManagers();
    }
  }, [navigate]);

  const fetchApplications = async () => {
    try {
      
      const response = await axios.get(`${API}/admin/applications`, {
        headers: {}
      });
      setApplications(response.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      
      const response = await axios.get(`${API}/admin/managers/stats`, {
        headers: {}
      });
      setManagers(response.data);
    } catch (err) {
      console.error("Failed to fetch managers:", err);
      setManagers([]);
    }
  };

  const handleApplication = async (applicationId) => {
    setHandlingAppId(applicationId);
    try {
      
      await axios.post(
        `${API}/admin/applications/${applicationId}/handle`,
        {},
        { headers: {} }
      );
      // Refresh applications to get updated data
      await fetchApplications();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to handle application");
    } finally {
      setHandlingAppId(null);
    }
  };

  const reassignApplication = async (applicationId, managerId) => {
    setReassigningAppId(applicationId);
    try {
      
      await axios.post(
        `${API}/admin/applications/${applicationId}/reassign`,
        { manager_id: managerId },
        { headers: {} }
      );
      // Refresh applications
      await fetchApplications();
      // Refresh manager stats
      await fetchManagers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reassign application");
    } finally {
      setReassigningAppId(null);
    }
  };

  // Lazy load photo for an application
  const loadPhoto = async (applicationId) => {
    if (photoCache[applicationId] || loadingPhotos[applicationId]) return;
    
    setLoadingPhotos(prev => ({ ...prev, [applicationId]: true }));
    try {
      
      const response = await axios.get(
        `${API}/admin/applications/${applicationId}/photo`,
        { headers: {} }
      );
      
      if (response.data.photo_data) {
        setPhotoCache(prev => ({ ...prev, [applicationId]: response.data.photo_data }));
      }
    } catch (err) {
      console.error("Failed to load photo:", err);
    } finally {
      setLoadingPhotos(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  // Fetch status history for an application (admin only)
  const fetchStatusHistory = async (applicationId, applicationName) => {
    if (user?.role !== "admin") return;
    
    setStatusHistoryModal({ applicationId, applicationName, history: [], loading: true, createdAt: null });
    try {
      
      const response = await axios.get(
        `${API}/admin/applications/${applicationId}/status-history`,
        { headers: {} }
      );
      setStatusHistoryModal({
        applicationId,
        applicationName,
        history: response.data.status_history || [],
        currentStatus: response.data.current_status,
        createdAt: response.data.created_at,
        loading: false
      });
    } catch (err) {
      setStatusHistoryModal(null);
      alert("Failed to load status history");
    }
  };

  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookies
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.error("Logout API call failed:", err.message);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const updateStatus = async (applicationId, newStatus) => {
    setUpdatingId(applicationId);
    try {
      
      await axios.patch(
        `${API}/admin/applications/${applicationId}/status`,
        { status: newStatus },
        {
          headers: {}
        }
      );
      
      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId ? { ...app, status: newStatus } : app
        )
      );
    } catch (err) {
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!window.confirm("Are you sure you want to delete this application? This action cannot be undone.")) {
      return;
    }
    
    setDeletingId(applicationId);
    try {
      
      await axios.delete(
        `${API}/admin/applications/${applicationId}`,
        {
          headers: {}
        }
      );
      
      // Remove from local state
      setApplications(prev => prev.filter(app => app.id !== applicationId));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete application");
    } finally {
      setDeletingId(null);
    }
  };

  const startEditingComment = (app) => {
    setEditingCommentId(app.id);
    setCommentText(app.internal_comments || "");
  };

  const cancelEditingComment = () => {
    setEditingCommentId(null);
    setCommentText("");
  };

  const saveComment = async (applicationId) => {
    setSavingComment(true);
    try {
      
      await axios.patch(
        `${API}/admin/applications/${applicationId}/comments`,
        { internal_comments: commentText },
        {
          headers: {}
        }
      );
      
      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId ? { ...app, internal_comments: commentText } : app
        )
      );
      setEditingCommentId(null);
      setCommentText("");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save comment");
    } finally {
      setSavingComment(false);
    }
  };

  const toggleInteresting = async (applicationId, currentValue) => {
    if (user?.role !== "admin") {
      alert("Only admins can mark applications as interesting");
      return;
    }
    
    setTogglingInterestingId(applicationId);
    try {
      
      await axios.patch(
        `${API}/admin/applications/${applicationId}/interesting`,
        { is_interesting: !currentValue },
        {
          headers: {}
        }
      );
      
      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId ? { ...app, is_interesting: !currentValue } : app
        )
      );
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update application");
    } finally {
      setTogglingInterestingId(null);
    }
  };

  const handlePdfUpload = async (applicationId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (file.type !== "application/pdf") {
      alert("Please select a PDF file");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      alert("File too large. Maximum size is 10MB");
      return;
    }
    
    setUploadingPdfId(applicationId);
    try {
      
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(
        `${API}/admin/applications/${applicationId}/pdf`,
        formData,
        {
          headers: { 
            
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      // Update local state
      setApplications(prev => 
        prev.map(app => {
          if (app.id === applicationId) {
            const pdfFiles = app.pdf_files || [];
            return { ...app, pdf_files: [...pdfFiles, response.data.pdf] };
          }
          return app;
        })
      );
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to upload PDF");
    } finally {
      setUploadingPdfId(null);
      // Reset input
      if (pdfInputRefs.current[applicationId]) {
        pdfInputRefs.current[applicationId].value = "";
      }
    }
  };

  const handlePdfDelete = async (applicationId, pdfId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) return;
    
    setDeletingPdfId(pdfId);
    try {
      
      await axios.delete(
        `${API}/admin/applications/${applicationId}/pdf/${pdfId}`,
        {
          headers: {}
        }
      );
      
      // Update local state
      setApplications(prev => 
        prev.map(app => {
          if (app.id === applicationId) {
            return { 
              ...app, 
              pdf_files: (app.pdf_files || []).filter(p => p.id !== pdfId) 
            };
          }
          return app;
        })
      );
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete PDF");
    } finally {
      setDeletingPdfId(null);
    }
  };

  const downloadPdf = (pdfData, filename) => {
    const link = document.createElement("a");
    link.href = `data:application/pdf;base64,${pdfData}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const createCreatorFromApplication = async (applicationId) => {
    if (!window.confirm("Create a Creator account from this application? You will need to send the login credentials separately from the Users page.")) {
      return;
    }
    
    setCreatingCreatorId(applicationId);
    try {
      
      const response = await axios.post(
        `${API}/admin/applications/${applicationId}/create-creator`,
        {},
        {
          headers: {}
        }
      );
      
      // Show modal with creator details
      setCreatorModal(response.data.creator);
      
      // Update local state to mark application as converted
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? { ...app, converted_to_creator: true, creator_user_id: response.data.creator.id }
            : app
        )
      );
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create creator account");
    } finally {
      setCreatingCreatorId(null);
    }
  };

  const toggleLandingPage = async (creatorId, currentActive) => {
    setTogglingLandingPageId(creatorId);
    try {
      
      await axios.patch(
        `${API}/admin/creators/${creatorId}/landing-page`,
        { landing_page_active: !currentActive },
        {
          headers: {}
        }
      );
      
      // Update local state
      setApplications(prev => 
        prev.map(app => {
          if (app.creator_user_id === creatorId) {
            return { ...app, creator_landing_page_active: !currentActive };
          }
          return app;
        })
      );
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update landing page status");
    } finally {
      setTogglingLandingPageId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const openEditCreatorModal = (app) => {
    setEditCreatorForm({
      id: app.creator_user_id,
      name: app.creator_name || app.name,
      landing_page_slug: app.landing_page_slug || "",
      landing_page_headline: app.creator_headline || `Follow ${app.creator_name || app.name}`,
      bio: app.creator_bio || "",
      instagram: app.creator_instagram || app.instagram || "",
      twitter: app.creator_twitter || app.twitter || "",
      tiktok: app.creator_tiktok || app.tiktok || "",
      onlyfans: app.creator_onlyfans || app.onlyfans || "",
      website: app.creator_website || "",
      profile_image: app.creator_profile_image || photoCache[app.id] || null
    });
    setEditCreatorModal(app);
  };

  const closeEditCreatorModal = () => {
    setEditCreatorModal(null);
    setEditCreatorForm({});
  };

  const handleEditFormChange = (field, value) => {
    setEditCreatorForm(prev => ({ ...prev, [field]: value }));
  };

  const handleProfileImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert("Image too large. Maximum size is 5MB");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(",")[1];
      setEditCreatorForm(prev => ({ ...prev, profile_image: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const saveCreatorChanges = async () => {
    setSavingCreator(true);
    try {
      
      const response = await axios.patch(
        `${API}/admin/creators/${editCreatorForm.id}/landing-page`,
        {
          name: editCreatorForm.name,
          landing_page_slug: editCreatorForm.landing_page_slug,
          landing_page_headline: editCreatorForm.landing_page_headline,
          bio: editCreatorForm.bio,
          instagram: editCreatorForm.instagram,
          twitter: editCreatorForm.twitter,
          tiktok: editCreatorForm.tiktok,
          onlyfans: editCreatorForm.onlyfans,
          website: editCreatorForm.website,
          profile_image: editCreatorForm.profile_image
        },
        {
          headers: {}
        }
      );
      
      // Update local state
      const updatedCreator = response.data.creator;
      setApplications(prev => 
        prev.map(app => {
          if (app.creator_user_id === editCreatorForm.id) {
            return {
              ...app,
              landing_page_slug: updatedCreator.landing_page_slug,
              creator_name: updatedCreator.name,
              creator_headline: updatedCreator.landing_page_headline,
              creator_bio: updatedCreator.bio,
              creator_instagram: updatedCreator.instagram,
              creator_twitter: updatedCreator.twitter,
              creator_tiktok: updatedCreator.tiktok,
              creator_onlyfans: updatedCreator.onlyfans,
              creator_website: updatedCreator.website,
              creator_profile_image: updatedCreator.profile_image
            };
          }
          return app;
        })
      );
      
      closeEditCreatorModal();
      alert("Creator updated successfully!");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update creator");
    } finally {
      setSavingCreator(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.country?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || app.status === statusFilter;
    
    const matchesInteresting = 
      interestingFilter === "all" || 
      (interestingFilter === "interesting" && app.is_interesting) ||
      (interestingFilter === "not_interesting" && !app.is_interesting);
    
    return matchesSearch && matchesStatus && matchesInteresting;
  });

  // Pagination
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedApplications = filteredApplications.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, interestingFilter]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === "pending").length,
    contacted: applications.filter(a => a.status === "contacted").length,
    meeting_scheduled: applications.filter(a => a.status === "meeting_scheduled").length,
    meeting_canceled: applications.filter(a => a.status === "meeting_canceled").length,
    meeting_performed: applications.filter(a => a.status === "meeting_performed").length,
    no_show: applications.filter(a => a.status === "no_show").length,
    deal: applications.filter(a => a.status === "deal").length,
    closed: applications.filter(a => a.status === "closed").length,
    not_a_fit: applications.filter(a => a.status === "not_a_fit").length,
    not_interested: applications.filter(a => a.status === "not_interested").length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00A8E8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-gray-800/50 border-b border-gray-700/50 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
                <span className="text-white">HotPeek</span>
              </h1>
              <nav className="flex items-center gap-1">
                <Link
                  to="/admin"
                  className="flex items-center gap-2 px-3 py-2 text-white bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-applications"
                >
                  <FileStack size={18} />
                  <span className="hidden sm:inline">Applications</span>
                </Link>
                {user?.role === "admin" && (
                  <>
                    <Link
                      to="/admin/creator-requests"
                      className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                      data-testid="nav-creator-requests"
                    >
                      <Star size={18} />
                      <span className="hidden sm:inline">Creator Requests</span>
                    </Link>
                    <Link
                      to="/admin/users"
                      className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                      data-testid="nav-users"
                    >
                      <Users size={18} />
                      <span className="hidden sm:inline">Users</span>
                    </Link>
                    <Link
                      to="/admin/packages"
                      className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                      data-testid="nav-packages"
                    >
                      <Package size={18} />
                      <span className="hidden sm:inline">Packages</span>
                    </Link>
                    <Link
                      to="/admin/transactions"
                      className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                      data-testid="nav-transactions"
                    >
                      <CreditCard size={18} />
                      <span className="hidden sm:inline">Transactions</span>
                    </Link>
                    <Link
                      to="/admin/settings"
                      className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                      data-testid="nav-settings"
                    >
                      <Settings size={18} />
                      <span className="hidden sm:inline">Settings</span>
                    </Link>
                  </>
                )}
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{user?.name}</p>
                <p className="text-gray-500 text-sm">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Logout"
                data-testid="logout-btn"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-purple-500/10 rounded-lg">
                <Users size={16} className="text-purple-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Total</p>
                <p className="text-xl font-bold text-white">{stats.total}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-amber-500/10 rounded-lg">
                <Clock size={16} className="text-amber-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Pending</p>
                <p className="text-xl font-bold text-white">{stats.pending}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-500/10 rounded-lg">
                <Check size={16} className="text-blue-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Contacted</p>
                <p className="text-xl font-bold text-white">{stats.contacted}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-purple-500/10 rounded-lg">
                <CalendarCheck size={16} className="text-purple-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Scheduled</p>
                <p className="text-xl font-bold text-white">{stats.meeting_scheduled}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-orange-500/10 rounded-lg">
                <CalendarX size={16} className="text-orange-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Canceled</p>
                <p className="text-xl font-bold text-white">{stats.meeting_canceled}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-indigo-500/10 rounded-lg">
                <Video size={16} className="text-indigo-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Performed</p>
                <p className="text-xl font-bold text-white">{stats.meeting_performed}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-rose-500/10 rounded-lg">
                <UserX size={16} className="text-rose-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">No Show</p>
                <p className="text-xl font-bold text-white">{stats.no_show}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-500/10 rounded-lg">
                <Handshake size={16} className="text-green-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Deal</p>
                <p className="text-xl font-bold text-white">{stats.deal}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-gray-500/10 rounded-lg">
                <XCircle size={16} className="text-gray-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Closed</p>
                <p className="text-xl font-bold text-white">{stats.closed}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-red-500/10 rounded-lg">
                <ThumbsDown size={16} className="text-red-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Not a Fit</p>
                <p className="text-xl font-bold text-white">{stats.not_a_fit}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="bg-gray-800/50 rounded-xl p-3 border border-gray-700/50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-slate-500/10 rounded-lg">
                <XCircle size={16} className="text-slate-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs">Not Int.</p>
                <p className="text-xl font-bold text-white">{stats.not_interested}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or country..."
              className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all"
              data-testid="search-input"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-10 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="meeting_scheduled">Meeting Scheduled</option>
              <option value="meeting_canceled">Meeting Canceled</option>
              <option value="meeting_performed">Meeting Performed</option>
              <option value="no_show">No Show</option>
              <option value="deal">Deal</option>
              <option value="closed">Closed</option>
              <option value="not_a_fit">Not a Fit</option>
              <option value="not_interested">Not Interested</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={18} />
          </div>
          <div className="relative">
            <Star className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <select
              value={interestingFilter}
              onChange={(e) => setInterestingFilter(e.target.value)}
              className="pl-10 pr-10 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all"
              data-testid="interesting-filter"
            >
              <option value="all">All Applications</option>
              <option value="interesting">Interesting</option>
              <option value="not_interesting">Not Marked</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={18} />
          </div>
        </div>

        {/* Applications List */}
        <div className="space-y-4">
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-500">No applications found</p>
            </div>
          ) : (
            paginatedApplications.map((app, index) => {
              const status = statusConfig[app.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              return (
                <motion.div
                  key={app.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden hover:border-gray-600/50 transition-colors"
                  data-testid={`application-card-${app.id}`}
                >
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      {/* Photo - Lazy loaded */}
                      <LazyPhoto 
                        applicationId={app.id}
                        photoData={photoCache[app.id]}
                        isLoading={loadingPhotos[app.id]}
                        onVisible={() => loadPhoto(app.id)}
                        name={app.name}
                      />
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-white truncate">{app.name}</h3>
                          {app.is_interesting && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                              <Star size={12} fill="currentColor" />
                              Interesting
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${status.bg} ${status.text} ${status.border}`}>
                            <StatusIcon size={12} />
                            {status.label}
                          </span>
                          {/* Assigned Manager Badge */}
                          {app.assigned_manager_name && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                              <UserCheck size={12} />
                              {app.assigned_manager_name}
                            </span>
                          )}
                        </div>
                        
                        {/* Contact Info - Hidden for managers if not assigned */}
                        {app.contact_hidden ? (
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                            <span className="flex items-center gap-2 text-gray-500">
                              <Lock size={14} className="flex-shrink-0" />
                              <span className="blur-sm select-none">••••••••</span>
                            </span>
                            <span className="flex items-center gap-2 text-gray-500">
                              <Lock size={14} className="flex-shrink-0" />
                              <span className="blur-sm select-none">••••••••</span>
                            </span>
                            <span className="flex items-center gap-2 text-gray-400">
                              <MapPin size={14} className="flex-shrink-0" />
                              {app.country}
                            </span>
                            <span className="flex items-center gap-2 text-gray-500 text-xs">
                              <Calendar size={14} className="flex-shrink-0" />
                              {formatDate(app.created_at)}
                            </span>
                          </div>
                        ) : (
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                            <a href={`mailto:${app.email}`} className="flex items-center gap-2 text-gray-400 hover:text-[#00A8E8] transition-colors truncate">
                              <Mail size={14} className="flex-shrink-0" />
                              {app.email}
                            </a>
                            <a href={`https://wa.me/${app.whatsapp?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-green-400 transition-colors">
                              <Phone size={14} className="flex-shrink-0" />
                              {app.whatsapp}
                            </a>
                            <span className="flex items-center gap-2 text-gray-400">
                              <MapPin size={14} className="flex-shrink-0" />
                              {app.country}
                            </span>
                            {app.instagram && (
                              <a href={`https://instagram.com/${app.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-pink-400 transition-colors">
                                <Instagram size={14} className="flex-shrink-0" />
                                {app.instagram}
                              </a>
                            )}
                            <span className="flex items-center gap-2 text-gray-500 text-xs">
                              <Calendar size={14} className="flex-shrink-0" />
                              {formatDate(app.created_at)}
                            </span>
                          </div>
                        )}
                        
                        {app.message && !app.contact_hidden && (
                          <p className="mt-3 text-gray-500 text-sm line-clamp-2">{app.message}</p>
                        )}
                        {app.contact_hidden && (
                          <p className="mt-3 text-gray-500 text-sm italic">Click "Handle" to reveal contact information</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                        {/* Handle Application button - for managers when contact is hidden */}
                        {user?.role === "manager" && app.contact_hidden && !app.assigned_manager_id && (
                          <button
                            onClick={() => handleApplication(app.id)}
                            disabled={handlingAppId === app.id}
                            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-sm font-medium"
                            title="Handle this application to reveal contact info"
                            data-testid={`handle-btn-${app.id}`}
                          >
                            {handlingAppId === app.id ? (
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              <UserCheck size={16} />
                            )}
                            Handle
                          </button>
                        )}
                        
                        {/* Reassign dropdown - admin only */}
                        {user?.role === "admin" && (
                          <div className="relative">
                            <select
                              value={app.assigned_manager_id || ""}
                              onChange={(e) => {
                                if (e.target.value) {
                                  reassignApplication(app.id, e.target.value);
                                }
                              }}
                              disabled={reassigningAppId === app.id}
                              className="pl-3 pr-8 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:opacity-50"
                              title="Assign/Reassign to manager"
                              data-testid={`reassign-select-${app.id}`}
                            >
                              <option value="">Assign to...</option>
                              {managers.map((manager) => (
                                <option key={manager.id} value={manager.id}>
                                  {manager.name} ({manager.email})
                                </option>
                              ))}
                            </select>
                            {reassigningAppId === app.id ? (
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-gray-500/30 border-t-gray-500 rounded-full animate-spin" />
                            ) : (
                              <RefreshCw size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                            )}
                          </div>
                        )}
                        
                        {app.view_key && (
                          <Link
                            to={`/view/${app.view_key}`}
                            target="_blank"
                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                            title="View full application"
                          >
                            <Eye size={18} />
                          </Link>
                        )}
                        
                        {/* Star/Interesting toggle - admin only */}
                        {user?.role === "admin" && (
                          <button
                            onClick={() => toggleInteresting(app.id, app.is_interesting)}
                            disabled={togglingInterestingId === app.id}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                              app.is_interesting 
                                ? "text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20" 
                                : "text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10"
                            }`}
                            title={app.is_interesting ? "Remove interesting tag" : "Mark as interesting"}
                            data-testid={`interesting-btn-${app.id}`}
                          >
                            {togglingInterestingId === app.id ? (
                              <div className="w-[18px] h-[18px] border-2 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin" />
                            ) : (
                              <Star size={18} fill={app.is_interesting ? "currentColor" : "none"} />
                            )}
                          </button>
                        )}
                        
                        {/* Create Creator button - admin only */}
                        {user?.role === "admin" && !app.converted_to_creator && (
                          <button
                            onClick={() => createCreatorFromApplication(app.id)}
                            disabled={creatingCreatorId === app.id}
                            className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Create Creator account"
                            data-testid={`create-creator-btn-${app.id}`}
                          >
                            {creatingCreatorId === app.id ? (
                              <div className="w-[18px] h-[18px] border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
                            ) : (
                              <UserPlus size={18} />
                            )}
                          </button>
                        )}
                        
                        {/* Creator Badge & Landing Page toggle - if converted */}
                        {app.converted_to_creator && (
                          <div className="flex items-center gap-1">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                              <UserPlus size={10} />
                              Creator
                            </span>
                          </div>
                        )}
                        
                        {/* Delete button - admin only */}
                        {user?.role === "admin" && (
                          <button
                            onClick={() => deleteApplication(app.id)}
                            disabled={deletingId === app.id}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete application"
                            data-testid={`delete-btn-${app.id}`}
                          >
                            {deletingId === app.id ? (
                              <div className="w-[18px] h-[18px] border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                            ) : (
                              <Trash2 size={18} />
                            )}
                          </button>
                        )}
                        
                        {/* Status History button - admin only */}
                        {user?.role === "admin" && (
                          <button
                            onClick={() => fetchStatusHistory(app.id, app.name)}
                            className="p-2 text-gray-400 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                            title="View status history"
                            data-testid={`history-btn-${app.id}`}
                          >
                            <History size={18} />
                          </button>
                        )}
                        
                        <div className="relative">
                          <select
                            value={app.status}
                            onChange={(e) => updateStatus(app.id, e.target.value)}
                            disabled={updatingId === app.id}
                            className={`pl-3 pr-8 py-2 rounded-lg text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 transition-all ${status.bg} ${status.text} border ${status.border} disabled:opacity-50`}
                            data-testid={`status-select-${app.id}`}
                          >
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="meeting_scheduled">Meeting Scheduled</option>
                            <option value="meeting_canceled">Meeting Canceled</option>
                            <option value="meeting_performed">Meeting Performed</option>
                            <option value="no_show">No Show</option>
                            <option value="deal">Deal</option>
                            <option value="closed">Closed</option>
                            <option value="not_a_fit">Not a Fit</option>
                            <option value="not_interested">Not Interested</option>
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" size={14} />
                        </div>
                      </div>
                    </div>
                    
                    {/* Internal Comments Section */}
                    <div className="mt-4 pt-4 border-t border-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare size={14} className="text-gray-500" />
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Internal Comments</span>
                      </div>
                      
                      {editingCommentId === app.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder="Add internal notes about this application..."
                            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg py-2 px-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00A8E8] resize-none"
                            rows={3}
                            data-testid={`comment-textarea-${app.id}`}
                          />
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => saveComment(app.id)}
                              disabled={savingComment}
                              className="flex items-center gap-1 px-3 py-1.5 bg-[#00A8E8] text-white text-sm font-medium rounded-lg hover:bg-[#00A8E8]/80 transition-colors disabled:opacity-50"
                              data-testid={`save-comment-btn-${app.id}`}
                            >
                              {savingComment ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              ) : (
                                <Save size={14} />
                              )}
                              Save
                            </button>
                            <button
                              onClick={cancelEditingComment}
                              className="px-3 py-1.5 text-gray-400 text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          onClick={() => startEditingComment(app)}
                          className="cursor-pointer group"
                        >
                          {app.internal_comments ? (
                            <p className="text-sm text-gray-400 whitespace-pre-wrap group-hover:text-gray-300 transition-colors">
                              {app.internal_comments}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-600 italic group-hover:text-gray-500 transition-colors">
                              Click to add internal notes...
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* PDF Files Section - Admin Only */}
                    {user?.role === "admin" && (
                      <div className="mt-4 pt-4 border-t border-gray-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="text-gray-500" />
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">PDF Documents</span>
                            {app.pdf_files?.length > 0 && (
                              <span className="text-xs bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">
                                {app.pdf_files.length}
                              </span>
                            )}
                          </div>
                          <label className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#00A8E8] hover:bg-[#00A8E8]/10 rounded cursor-pointer transition-colors">
                            {uploadingPdfId === app.id ? (
                              <div className="w-3 h-3 border-2 border-[#00A8E8]/30 border-t-[#00A8E8] rounded-full animate-spin" />
                            ) : (
                              <Upload size={12} />
                            )}
                            Upload PDF
                            <input
                              ref={el => pdfInputRefs.current[app.id] = el}
                              type="file"
                              accept="application/pdf"
                              onChange={(e) => handlePdfUpload(app.id, e)}
                              className="hidden"
                              disabled={uploadingPdfId === app.id}
                            />
                          </label>
                        </div>
                        
                        {app.pdf_files?.length > 0 ? (
                          <div className="space-y-2">
                            {app.pdf_files.map(pdf => (
                              <div 
                                key={pdf.id}
                                className="flex items-center justify-between bg-gray-800/30 rounded-lg px-3 py-2"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText size={16} className="text-red-400 flex-shrink-0" />
                                  <span className="text-sm text-gray-300 truncate">{pdf.filename}</span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {pdf.data && (
                                    <button
                                      onClick={() => downloadPdf(pdf.data, pdf.filename)}
                                      className="p-1.5 text-gray-400 hover:text-[#00A8E8] hover:bg-gray-700 rounded transition-colors"
                                      title="Download"
                                    >
                                      <Download size={14} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handlePdfDelete(app.id, pdf.id, pdf.filename)}
                                    disabled={deletingPdfId === pdf.id}
                                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                                    title="Delete"
                                  >
                                    {deletingPdfId === pdf.id ? (
                                      <div className="w-3.5 h-3.5 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                                    ) : (
                                      <X size={14} />
                                    )}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600 italic">No PDF files uploaded</p>
                        )}
                      </div>
                    )}
                    
                    {/* Creator Landing Page Section - if converted */}
                    {app.converted_to_creator && app.creator_user_id && (
                      <div className="mt-4 pt-4 border-t border-gray-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Globe size={14} className="text-cyan-500" />
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Creator Landing Page</span>
                          </div>
                          <button
                            onClick={() => toggleLandingPage(app.creator_user_id, app.creator_landing_page_active)}
                            disabled={togglingLandingPageId === app.creator_user_id}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                              app.creator_landing_page_active
                                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                : "bg-gray-700/50 text-gray-400 border border-gray-600/30"
                            }`}
                            data-testid={`toggle-landing-${app.id}`}
                          >
                            {togglingLandingPageId === app.creator_user_id ? (
                              <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                            ) : app.creator_landing_page_active ? (
                              <ToggleRight size={14} />
                            ) : (
                              <ToggleLeft size={14} />
                            )}
                            {app.creator_landing_page_active ? "Active" : "Inactive"}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg px-3 py-2">
                          <LinkIcon size={14} className="text-gray-500" />
                          <code className="text-cyan-400 text-sm flex-1">/c/{app.landing_page_slug || 'N/A'}</code>
                          <button
                            onClick={() => openEditCreatorModal(app)}
                            className="p-1 text-gray-400 hover:text-amber-400 transition-colors"
                            title="Edit creator profile"
                            data-testid={`edit-creator-btn-${app.id}`}
                          >
                            <Edit3 size={14} />
                          </button>
                          <a
                            href={`/c/${app.landing_page_slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-gray-400 hover:text-cyan-400 transition-colors"
                            title="Open landing page"
                          >
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-700/50">
            <p className="text-sm text-gray-500">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredApplications.length)} of {filteredApplications.length} applications
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="prev-page"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? "bg-[#00A8E8] text-white"
                          : "text-gray-400 hover:text-white hover:bg-gray-700"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="next-page"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </main>
      
      {/* Edit Creator Modal */}
      {editCreatorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800 rounded-2xl border border-gray-700 shadow-2xl max-w-2xl w-full p-6 my-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Edit3 className="text-amber-400" size={20} />
                Edit Creator Profile
              </h3>
              <button
                onClick={closeEditCreatorModal}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {/* Profile Image */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  {editCreatorForm.profile_image ? (
                    <img
                      src={getImageSrc(editCreatorForm.profile_image)}
                      alt="Profile"
                      className="w-20 h-20 rounded-xl object-cover border-2 border-gray-600"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gray-700 border-2 border-gray-600 flex items-center justify-center">
                      <User size={32} className="text-gray-500" />
                    </div>
                  )}
                  <button
                    onClick={() => profileImageInputRef.current?.click()}
                    className="absolute -bottom-1 -right-1 p-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                    title="Change image"
                  >
                    <Image size={12} />
                  </button>
                  <input
                    ref={profileImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageUpload}
                    className="hidden"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-400 mb-1">Display Name</label>
                  <input
                    type="text"
                    value={editCreatorForm.name || ""}
                    onChange={(e) => handleEditFormChange("name", e.target.value)}
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                    placeholder="Creator name"
                  />
                </div>
              </div>

              {/* Custom Username/Slug */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Custom Username (URL)
                  <span className="text-gray-500 ml-1">- letters, numbers, underscores, hyphens only</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm">/c/</span>
                  <input
                    type="text"
                    value={editCreatorForm.landing_page_slug || ""}
                    onChange={(e) => handleEditFormChange("landing_page_slug", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                    className="flex-1 bg-gray-700/50 border border-gray-600 rounded-lg py-2 px-3 text-cyan-400 font-mono placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                    placeholder="username"
                    maxLength={30}
                  />
                </div>
              </div>

              {/* Headline */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Headline</label>
                <input
                  type="text"
                  value={editCreatorForm.landing_page_headline || ""}
                  onChange={(e) => handleEditFormChange("landing_page_headline", e.target.value)}
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-lg py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                  placeholder="Follow me for exclusive content..."
                />
              </div>

              {/* Bio/Description */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Description</label>
                <textarea
                  value={editCreatorForm.bio || ""}
                  onChange={(e) => handleEditFormChange("bio", e.target.value)}
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-lg py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 resize-none"
                  placeholder="Short bio or description..."
                  rows={3}
                />
              </div>

              {/* Social Links */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <p className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <AtSign size={14} />
                  Social Links
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">OnlyFans</label>
                    <input
                      type="text"
                      value={editCreatorForm.onlyfans || ""}
                      onChange={(e) => handleEditFormChange("onlyfans", e.target.value)}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg py-2 px-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      placeholder="username or URL"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Instagram</label>
                    <input
                      type="text"
                      value={editCreatorForm.instagram || ""}
                      onChange={(e) => handleEditFormChange("instagram", e.target.value)}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg py-2 px-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Twitter / X</label>
                    <input
                      type="text"
                      value={editCreatorForm.twitter || ""}
                      onChange={(e) => handleEditFormChange("twitter", e.target.value)}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg py-2 px-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">TikTok</label>
                    <input
                      type="text"
                      value={editCreatorForm.tiktok || ""}
                      onChange={(e) => handleEditFormChange("tiktok", e.target.value)}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg py-2 px-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      placeholder="@username"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Website</label>
                    <input
                      type="text"
                      value={editCreatorForm.website || ""}
                      onChange={(e) => handleEditFormChange("website", e.target.value)}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-lg py-2 px-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={closeEditCreatorModal}
                className="flex-1 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveCreatorChanges}
                disabled={savingCreator || !editCreatorForm.name?.trim() || !editCreatorForm.landing_page_slug?.trim()}
                className="flex-1 py-3 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {savingCreator ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Creator Created Modal */}
      {creatorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800 rounded-2xl border border-gray-700 shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <UserPlus className="text-cyan-400" size={20} />
                Creator Account Created
              </h3>
              <button
                onClick={() => setCreatorModal(null)}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-400 text-sm font-medium mb-2">Account Created Successfully</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Name:</span>
                    <span className="text-white text-sm font-medium">{creatorModal.name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Email:</span>
                    <span className="text-white text-sm font-medium">{creatorModal.email}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-amber-400 text-sm font-medium mb-2 flex items-center gap-2">
                  <Mail size={14} />
                  Send Login Credentials
                </p>
                <p className="text-gray-400 text-sm">
                  Go to <strong className="text-white">Users → Creators</strong> tab to send the login credentials to this creator via email.
                </p>
              </div>
              
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-gray-300 text-sm font-medium mb-2 flex items-center gap-2">
                  <Globe size={14} />
                  Landing Page URL
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-cyan-400 text-sm bg-gray-800 px-3 py-2 rounded-lg overflow-x-auto">
                    {window.location.origin}/c/{creatorModal.landing_page_slug}
                  </code>
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}/c/${creatorModal.landing_page_slug}`)}
                    className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Copy URL"
                  >
                    <Copy size={16} />
                  </button>
                  <a
                    href={`/c/${creatorModal.landing_page_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Open landing page"
                  >
                    <ExternalLink size={16} />
                  </a>
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  Note: The landing page needs to be activated before it becomes public.
                </p>
              </div>
              
              <button
                onClick={() => setCreatorModal(null)}
                className="w-full py-3 bg-cyan-500 text-white font-medium rounded-lg hover:bg-cyan-600 transition-colors"
              >
                Done
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Status History Modal - Admin Only */}
      {statusHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800 rounded-2xl w-full max-w-lg border border-gray-700 shadow-2xl my-8"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <History className="text-purple-400" size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Status History</h3>
                  <p className="text-sm text-gray-400">{statusHistoryModal.applicationName}</p>
                </div>
              </div>
              <button
                onClick={() => setStatusHistoryModal(null)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {statusHistoryModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Current Status */}
                  <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Current Status</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        statusConfig[statusHistoryModal.currentStatus]?.bg || "bg-gray-500/10"
                      } ${statusConfig[statusHistoryModal.currentStatus]?.text || "text-gray-400"}`}>
                        {statusConfig[statusHistoryModal.currentStatus]?.label || statusHistoryModal.currentStatus}
                      </span>
                    </div>
                  </div>

                  {/* Timeline */}
                  {statusHistoryModal.history && statusHistoryModal.history.length > 0 ? (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-700"></div>
                      <div className="space-y-4">
                        {[...statusHistoryModal.history].reverse().map((entry) => {
                          const fromStatus = statusConfig[entry.from_status] || { label: entry.from_status, text: "text-gray-400" };
                          const toStatus = statusConfig[entry.to_status] || { label: entry.to_status, text: "text-gray-400" };
                          const changedAt = new Date(entry.changed_at);
                          
                          return (
                            <div key={`${entry.changed_at}-${entry.from_status}-${entry.to_status}`} className="relative pl-10">
                              <div className="absolute left-2.5 w-3 h-3 bg-purple-500 rounded-full border-2 border-gray-800"></div>
                              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/30">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`text-sm ${fromStatus.text}`}>{fromStatus.label}</span>
                                  <ArrowRight size={14} className="text-gray-500" />
                                  <span className={`text-sm font-medium ${toStatus.text}`}>{toStatus.label}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                  <span>by {entry.changed_by_email}</span>
                                  <span>{changedAt.toLocaleDateString()} {changedAt.toLocaleTimeString()}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Application Created */}
                        {statusHistoryModal.createdAt && (
                          <div className="relative pl-10">
                            <div className="absolute left-2.5 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-800"></div>
                            <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/30">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-green-400">Application Created</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(statusHistoryModal.createdAt).toLocaleDateString()} {new Date(statusHistoryModal.createdAt).toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <History size={32} className="mx-auto text-gray-600 mb-3" />
                      <p className="text-gray-500">No status changes recorded yet</p>
                      <p className="text-gray-600 text-sm mt-1">Status changes will appear here</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-700">
              <button
                onClick={() => setStatusHistoryModal(null)}
                className="w-full py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
